// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { LinkedSystem } from '../misc/LinkedSystem';
import { PositionsBalanceItem } from '../cache/PositionsBalanceItem';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { DynProperty } from '@shared/commons/DynProperty';
import { DataCache } from '@shared/commons/DataCache';
import { ApplicationInfo } from '@shared/commons/ApplicationInfo';
import PositionsBalancePanelTemplate from '@front/templates/PositionsBalancePanelTemplate.html';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { PositionsBalanceManager } from '@shared/utils/Managers/PositionsBalanceManager';
import { _AccountItemAll } from '../elements/Lookup/TerceraAccountLookup';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class PositionsBalancePanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this.ToolbarVisible = true;
        this.ShowTotals = false;
        this.accLookup = null;
        this.delayAccounts = null;
        this.inited = false;
        this._balanceManager = new PositionsBalanceManager();
        this.OnAddPositionsBalance = (positionsBalance) => {
            const qt = this.getQuickTable();
            if (isNullOrUndefined(qt))
                return;
            const newItem = new PositionsBalanceItem(positionsBalance, SessionSettings);
            newItem.ItemId = positionsBalance.positionBalanceID.toString();
            const row = qt.AddItem(newItem);
            row.id = positionsBalance.positionBalanceID.toString();
            this.updatePanelHeader();
            qt.needRedrawBackground = true;
        };
        this.OnRemovePositionsBalance = (positionsBalance) => {
            const qt = this.getQuickTable();
            if (isNullOrUndefined(qt))
                return;
            const id = positionsBalance.positionBalanceID.toString();
            qt.RemoveItem(id);
            this.updatePanelHeader();
            qt.needRedrawBackground = true;
        };
        this.OnUpdatePositionsBalance = (positionsBalance) => {
            const qt = this.getQuickTable();
            if (isNullOrUndefined(qt))
                return;
            const id = positionsBalance.positionBalanceID.toString();
            const item = qt.getItemById(id);
            if (!isNullOrUndefined(item)) {
                item.item.Recalc();
            }
            this.updatePanelHeader();
            qt.needRedrawBackground = true;
        };
        this.moreThanOneTTKey = 'panel.positionsBalance.menu.MoreOneSymbolInfoDisabled.tt'; // #95439
        this.noOneTTKey = 'panel.positionsBalance.menu.NoSymbolInfoDisabled.tt';
        this.headerLocaleKey = 'panel.positionsBalance';
    }
    getType() {
        return PanelNames.PositionsBalancePanel;
    }
    oncomplete() {
        super.oncomplete();
        this.DCEventsSubscribe();
        this.populateTableContextMenu();
        this.localize();
        this.UpdateShowTotalsStateChange(!!this.ShowTotals);
    }
    AddAllRows() {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        qt.ClearAll();
        const accInUse = this.getAccInUse();
        this._balanceManager.filter(Object.keys(accInUse));
        this._balanceManager.run();
    }
    getAccInUse() {
        if (!this.accLookup) {
            return null;
        }
        let accArr = [];
        const selectedAccs = this.accLookup.get('selectedItem');
        if (selectedAccs._AccountItemAll) {
            const accounts = DataCache.getAccounts();
            for (const accID in accounts) {
                accArr.push(accounts[accID]);
            }
        }
        else if (selectedAccs.arraySelected) {
            accArr = selectedAccs.arraySelected;
        }
        else {
            accArr = [selectedAccs];
        }
        const accUse = {};
        for (let i = 0; i < accArr.length; i++) {
            accUse[accArr[i].AcctNumber] = true;
        }
        return accUse;
    }
    updatePanelHeader() {
        const qt = this.getQuickTable();
        if (!qt) {
            return;
        }
        const rowNumSt = '  (' + qt.visibleRowCount() + ')';
        const newHeader = Resources.getResource(this.headerLocaleKey) + rowNumSt;
        this.set('header', newHeader);
    }
    DCEventsSubscribe() {
        this._balanceManager.subscribeToAdd(this.OnAddPositionsBalance);
        this._balanceManager.subscribeToRemove(this.OnRemovePositionsBalance);
        this._balanceManager.subscribeToUpdate(this.OnUpdatePositionsBalance);
        this._balanceManager.run();
        DataCache.OnRecalculatedBalances.Subscribe(this.RecalculatedBalances, this);
    }
    DCEventsUnSubscribe() {
        this._balanceManager.stop();
        this._balanceManager.unsubscribeToAdd(this.OnAddPositionsBalance);
        this._balanceManager.unsubscribeToRemove(this.OnRemovePositionsBalance);
        this._balanceManager.unsubscribeToUpdate(this.OnUpdatePositionsBalance);
        DataCache.OnRecalculatedBalances.UnSubscribe(this.RecalculatedBalances, this);
    }
    jbInit() {
        super.jbInit();
        this.createAccountLookup();
        this.getQuickTable().OnSelectionChanged.Subscribe(this.selectionChange, this);
    }
    dispose() {
        this.getQuickTable().OnSelectionChanged.UnSubscribe(this.selectionChange, this);
        this.DCEventsUnSubscribe();
        super.dispose();
    }
    SetColumnsColouringMode(table) {
        super.ProcessSetColumnsColouringMode(table, [3], ColouringModes.Previous);
        super.ProcessSetColumnsColouringMode(table, [4], ColouringModes.Signed);
        super.ProcessSetColumnsColouringMode(table, [10], ColouringModes.Signed);
    }
    createAccountLookup() {
        const accountsLookup = this.Controls.accountSelector.getLookup();
        accountsLookup.onChangeValue.Subscribe(this.AddAllRows, this);
        accountsLookup.IsMultiSelect = true;
        accountsLookup.isMultiSelectMode = true;
        this.accLookup = accountsLookup;
        if (this.delayAccounts) {
            const accs = DataCache.Accounts;
            const accIDArr = this.delayAccounts.split(',');
            const accArr = [];
            for (let i = 0; i < accIDArr.length; i++) {
                if (accs[accIDArr[i]] || accIDArr[i] === 'All') {
                    accArr.push(accs[accIDArr[i]]);
                }
            }
            accountsLookup.dropDownFormCallBack(accArr);
            this.delayAccounts = null;
        }
        else {
            let accToInitWith = new _AccountItemAll();
            if (DataCache.EnableForceLinkingByAccount()) {
                const accID = LinkedSystem.getAccount();
                accToInitWith = [DataCache.GetAccountById(accID)];
            }
            accountsLookup.dropDownFormCallBack(accToInitWith);
        }
        this.ShowToolbarStateUpdate(this.ToolbarVisible);
    }
    selectionChange() {
        const qt = this.getQuickTable();
        const selectedRowsId = qt.selectedRowIds;
        if (!selectedRowsId)
            return;
        let insId = '';
        if (selectedRowsId.length > 0) {
            const row = qt.rows[selectedRowsId[0]];
            const item = row ? row.item : null;
            if (item) {
                const ins = item.GetCurrentInstrument();
                if (ins)
                    insId = ins.GetInteriorID();
            }
        }
        this.symbolLink_Out(false, insId);
    }
    accountLink_In(accId, fromLinkedSystem) {
        if (fromLinkedSystem && !this.OnAccountLinking()) {
            return false;
        }
        const acc = DataCache.Accounts[accId];
        if (acc && this.accLookup) {
            this.accLookup.set('selectedItem', acc);
        }
        this.AddAllRows();
        return true;
    }
    symbolLink_Out(newSubscriber, instrument) {
        if (!instrument) {
            return;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument, newSubscriber);
        }
    }
    populateTableContextMenu() {
        const items = [];
        const moreThanOnAcc = DataCache.getNumberOfAccounts() > 1;
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.positions.menu.View',
            svgImage: 'View',
            subitems: [
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.positions.menu.ShowTotals',
                    checked: this.ShowTotals,
                    visible: !Resources.isHidden('contextMenu.Totals.visibility'),
                    canCheck: true,
                    event: this.ShowTotalsStateChange.bind(this)
                },
                {
                    type: NewContextMenuItemType.Default,
                    text: 'panel.positionsBalance.menu.ShowToolbar',
                    checked: this.ToolbarVisible,
                    visible: !Resources.isHidden('panel.positionsBalance.menu.ShowToolbar') && moreThanOnAcc,
                    canCheck: true,
                    event: this.ShowToolbarStateChange.bind(this)
                }
            ]
        });
        this.AddSymbolInfoContextMenuItemIfNeed(items, false);
        this.menuTagDict = createTagDictionary(items);
        this.getQuickTable().setTableContextMenuItems(items);
    }
    ShowToolbarStateChange(menuItem) {
        this.ToolbarVisible = menuItem.checked;
        this.ShowToolbarStateUpdate(this.ToolbarVisible);
    }
    ShowToolbarStateUpdate(state) {
        if (DataCache.getNumberOfAccounts() === 1) {
            state = false;
        }
        this.accLookup.set('visible', state);
        this.layoutTable();
    }
    disableAccountLinking() {
        this.updateAccountLinkVisible(false);
    }
    Properties() {
        const properties = super.Properties();
        const acc = this.accLookup.get('selectedItem');
        if (!acc) {
            return properties;
        }
        let accNumber = '';
        if (acc._AccountItemAll || acc._AccountItemSelected) {
            accNumber = acc.GetDataForSave();
        }
        else {
            accNumber = acc.AcctNumber;
        }
        properties.push(new DynProperty('account', accNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('ShowTotals', this.getQuickTable().ShowTotals, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('ToolbarVisible', this.ToolbarVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const dp = DynProperty.getPropertyByName(newProperties, 'account');
        if (dp === null || dp === void 0 ? void 0 : dp.value) {
            this.delayAccounts = dp.value;
        }
        this.ShowTotals = DynProperty.getPropValue(newProperties, 'ShowTotals');
        this.ToolbarVisible = DynProperty.getPropValue(newProperties, 'ToolbarVisible');
    }
    RecalculatedBalances() {
        const qt = this.getQuickTable();
        if (!qt)
            return;
        const rows = qt.rows;
        for (const r in rows) {
            rows[r].item.Recalc();
        }
    }
}
ApplicationPanelWithTable.extendWith(PositionsBalancePanel, {
    data: function () {
        return {
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            isShowExploreTheAppLink: ApplicationInfo.isExploreMode
        };
    },
    partials: {
        bodyPartial: PositionsBalancePanelTemplate
    }
});
